<template>
  <div class="nested-view">
    <section class="section" v-if="brand.webShortcuts.length > 0">
      <div class="web-shortcuts">
        <a class="item"
          v-for="(shortcut, index) in brand.webShortcuts"
          :href="shortcut.url"
          :style="'color:' + shortcut.color"
          :key="index">
          <div class="icon material-icons" :style="'box-shadow: inset 0 0 0 2px ' + shortcut.color">
            {{ shortcut.icon }}
          </div>
          <div class="name">{{ shortcut.label }}</div>
        </a>
      </div>
    </section>

    <section class="section">
      <div class="brand">
        <div class="shortcuts">
          <a
            class="item"
            :class="{ 'is-hidden': shortcut.key === 'menu' }"
            v-for="(shortcut, index) in brand.shortcuts"
            :key="`shortcut#${index}`"
            @click="onClickShortcut(shortcut)"
          >
          <template v-if="shortcut.key !== 'transaction'">
            <div class="icon" :style="'background-image: url(' + shortcut.image + '), linear-gradient(180deg, ' + shortcut.color + ',' + shortcut.color + ')'"></div>
            <div class="label">{{ shortcut.name }}</div>
          </template>
          </a>
        </div>
        <div v-if="brand.keywords" class="keywords">
          <div class="item" v-for="(keyword, index) in brand.keywords.split(',')" :key="`keyword#${index}`">#{{ keyword }}</div>
        </div>
        <div class="desc" v-html="brand.desc"></div>
        <div class="meta">
          <div class="item">
            <i class="material-icons icon">attach_money</i>
            <div class="content">${{ brand.price[0] | properNumber }}～${{ brand.price[1] | properNumber }}</div>
          </div>
          <template v-if="brand.stores.length === 1">
            <a class="item" :href="'tel:' + brand.stores[0].tel">
              <i class="material-icons icon">call</i>
              <div class="content">{{ brand.stores[0].tel }}</div>
            </a>
            <a class="item" target="_blank" :href="'https://www.google.com/maps/?q=' + brand.stores[0].address">
              <i class="material-icons icon">map</i>
              <div class="content">{{ brand.stores[0].address }}</div>
            </a>
            <div class="item hours-toggle" ref="hoursToggle" v-on:click="toggleHours(brand.stores[0].idx)">
              <i class="material-icons icon">query_builder</i>
              <div class="content">{{ $t('brand.openTime') }}</div>
              <i class="material-icons more">expand_more</i>
            </div>
            <div class="hours" ref="hours">
              <div class="item" v-for="(hours, index) in brand.stores[0].openhour" :key="`hours#${index}`">
                <div class="content">{{ [hours.week, 'E'] | moment('ddd') }} {{ hours.time }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="item branch-toggle" ref="branchesToggle" v-on:click="toggleBranches">
              <i class="material-icons icon">place</i>
              <div class="content">{{ $t('brand.branch') }}</div>
              <i class="material-icons more">expand_more</i>
            </div>
            <div class="branches" ref="branches">
              <div class="branch" v-for="(store, index) in brand.stores" :key="`store#${index}`">
                <div class="item store-toggle" :ref="'storeToggle' + store.token" v-on:click="toggleStore(store.token)">
                  <i class="material-icons icon">store</i>
                  <div class="content">{{ store.name }}</div>
                  <i class="material-icons more">expand_more</i>
                </div>
                <div class="store-meta" :ref="'store' + store.token">
                  <a class="item" :href="'tel:' + store.tel">
                    <i class="material-icons icon">call</i>
                    <div class="content">{{ store.tel }}</div>
                  </a>
                  <a class="item" target="_blank" :href="'https://www.google.com/maps/?q=' + store.address">
                    <i class="material-icons icon">place</i>
                    <div class="content">{{ store.address }}</div>
                  </a>
                  <div class="item hours-toggle" :ref="'hoursToggle' + store.token" v-on:click="toggleHours(store.token)">
                    <i class="material-icons icon">query_builder</i>
                    <div class="content">{{ $t('brand.openTime') }}</div>
                    <i class="material-icons more">expand_more</i>
                  </div>
                  <div class="hours" :ref="'hours' + store.token">
                    <div class="item" v-for="(hours, index) in store.openhour" :key="`hours#${index}`">
                      <div class="content">{{ [hours.week, 'E'] | moment('ddd') }} {{ hours.time }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </section>

    <div class="section-title">{{ $t('brand.gallery') }}</div>
    <section class="section" v-if="brand.brandImage && brand.brandImage.length > 0">
      <div class="gallery">
        <div
          v-for="(image, index) in brand.brandImage"
          :key="'gallery#' + index"
          class="gallery-item"
          @click="openLightbox(index)"
        >
          <img :src="image.image_url_s || image.image_url" :alt="image.desc || ''">
        </div>
      </div>
      <vue-image-lightbox
        ref="lightbox"
        :media="lightboxImages"
        :show-light-box="false"
        :show-thumbs="false"
        :show-caption="true"
        :show-footer-count="false"
        :length-to-load-more="1"
        @onClosed="closeLightbox()"
      >
      <template #close>
        <i class="material-icons icon">close</i>
       </template>
      </vue-image-lightbox>
    </section>

    <div class="section-title">{{ $t('profile.vip') }}</div>
    <section class="section">
      <div class="vips">
        <div
          v-for="vip in brand.vips"
          class="item"
          :class="{
            'current-vip': vip.idx === card.vip.idx
          }"
          :key="vip.idx"
          v-on:click="toggleVip(vip, vip.idx === card.vip.idx)"
        >
          <div class="vip-fig" v-if="vip.idx === card.vip.idx">
            <img :src="card.image">
          </div>
          <div class="vip-info">
            <div class="vip-name">{{ vip.name }}</div>
            <div class="vip-meta">
              <span v-if="vip.idx === card.vip.idx">
                {{ $t('profile.current') }}<template v-if="card.vip.expire_date">{{ $t('profile.dueDate') }} {{ [card.vip.expire_date, 'YYYY/M/D'] | moment('l') }} {{ $t('profile.dueDateAppend') }}</template>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="gap"></div>

    <div class="section-title">{{ $t('profile.history') }}</div>
    <section class="section section--history">
      <div class="history">
        <div class="item" v-for="history in card.history.slice(0, historyLength)" :key="history.trans_time">
          <div class="content">{{ history.content }}</div>
          <div class="time">{{ history.trans_time | moment('calendar') }}</div>
        </div>
        <div class="item" v-if="card.history.length === 0">
          <div class="content">{{ $t('profile.historyEmpty') }}</div>
        </div>
        <a class="more" v-if="historyLength < card.history.length" v-on:click="loadMoreHistory">
          {{ $t('common.more') }}
        </a>
      </div>
    </section>

    <div class="gap"></div>

    <section
      class="section section-tool"
      v-if="user.cell !== ''"
    >
      <div class="profile">
        <div class="row justify-content-center" v-if="card.serial">
          <div>{{ $t('profile.serial') }}</div>
          <div class="px-2">{{ card.serial }}</div>
        </div>
        <div class="row justify-content-center" v-if="user.cell">
          <div>{{ $t('profile.mobile') }}</div>
          <div class="px-2">{{ user.cellFormated }}</div>
        </div>
      </div>
    </section>

    <template v-if="card.downloadAPP">
      <div class="gap"></div>

      <div class="section-title">{{ $t('profile.downloadApp') }}</div>
      <section class="section section-download">
        <a href="https://bnc.lt/ocard" class="link" target="_blank" v-on:click="download">
          <div class="row narrow-gutters align-items-center">
            <div class="col col-auto">
              <img class="appicon" src="../../../assets/img/appicon.svg" alt="Ocard App">
            </div>
            <div class="col">
              {{ $t('profile.downloadTitle') }}<br>
              <div class="cta">{{ $t('profile.download') }}</div>
            </div>
          </div>
        </a>
      </section>
    </template>

    <powered-by></powered-by>

    <vip-detail
      :visable="vipDetailDisplay"
      :vip="vipDetail"
      :current="vipDetailIsCurrent"
      v-on:closeVIP="closeVIP()"
    ></vip-detail>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import Mixin from '@/mixins';
import VueImageLightbox from 'vue-image-lightbox';
import 'vue-image-lightbox/dist/vue-image-lightbox.min.css';

import PoweredBy from '@/components/PoweredBy.vue';
import VipDetail from '@/components/VipDetail.vue';

export default {
  name: 'Profile',
  mixins: [Mixin],
  components: {
    VueImageLightbox,
    PoweredBy,
    VipDetail,
  },
  computed: {
    ...mapState([
      'brand',
      'card',
      'user',
    ]),
    lightboxImages() {
      if (!this.brand.brandImage || this.brand.brandImage.length === 0) {
        return [];
      }
      return this.brand.brandImage.map(img => ({
        thumb: img.image_url_s || img.image_url,
        src: img.image_url_original || img.image_url,
        caption: this.$i18n.t('profile.mightBeSubjectToCopyright'),
      }));
    },
  },
  data() {
    return {
      vipDetailDisplay: false,
      vipDetail: {},
      vipDetailIsCurrent: false,
      historyLength: 3,
      scrollPosition: 0,
      currentIndex: 0,
    };
  },
  methods: {
    ...mapMutations([
      'setLoader',
    ]),
    toggleVip(vip, isCurrent) {
      this.vipDetail = vip;
      this.vipDetailIsCurrent = isCurrent;
      this.vipDetailDisplay = true;
    },
    closeVIP() {
      this.vipDetailDisplay = false;
    },
    loadMoreHistory() {
      this.historyLength += 3;
    },
    download() {
      this.$gtm.trackEvent({
        event: '下載 Ocard APP',
        label: this.$root.$store.state.brand.name,
      });
    },
    toggleBranches() {
      this.$refs.branchesToggle.classList.toggle('expended');
      this.$refs.branches.classList.toggle('expended');
    },
    toggleStore(index) {
      this.$refs[`storeToggle${index}`][0].classList.toggle('expended');
      this.$refs[`store${index}`][0].classList.toggle('expended');
    },
    toggleHours(index) {
      if (this.brand.stores.length > 1) {
        this.$refs[`hoursToggle${index}`][0].classList.toggle('expended');
        this.$refs[`hours${index}`][0].classList.toggle('expended');
      } else {
        this.$refs.hoursToggle.classList.toggle('expended');
        this.$refs.hours.classList.toggle('expended');
      }
    },
    openLightbox(index) {
      this.currentIndex = index;
      this.scrollPosition = window.scrollY;

      this.$refs.lightbox.showImage(index);
    },
    onClickShortcut(shortcut) {
      if (shortcut.key === 'menu') return;

      if (shortcut.key === 'transaction') {
        this.$router.push({ name: 'Transactions' });
      } else {
        window.open(shortcut.value, '_blank');
      }
    },
    closeLightbox() {
      this.$nextTick(() => {
        window.scrollTo({
          top: this.scrollPosition,
          behavior: 'instant',
        });
      });
    },
  },
};
</script>

<style lang="sass" scoped>
.section-title
  margin: 24px 24px 8px

  color: #686868
  font-size: 0.875rem
  font-weight: bold
  letter-spacing: 2px
  text-transform: uppercase

.web-shortcuts
  padding: 15px 5px
  display: flex
  flex-wrap: wrap
  justify-content: space-between

  .item
    margin: 0 2% 10px
    min-width: 46%
    flex-grow: 1
    height: 40px
    display: flex
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, .2)

    border-radius: 20px

    background-color: white

    &:nth-last-child(-n+2)
      margin-bottom: 0

    &:hover
      text-decoration: none
      opacity: .9

  .icon
    margin: 1px 0 0 1px
    width: 38px
    min-width: 38px
    height: 38px

    border-radius: 50%

    line-height: 36px
    text-align: center
    vertical-align: top

  .name
    flex-grow: 1
    padding: 0 8px

    line-height: 40px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    color: #252E35
    font-size: 0.916rem
    font-weight: bold

.shortcuts
  margin: 24px 0 0

  white-space: nowrap
  overflow-y: auto
  -webkit-overflow-scrolling: touch

  text-align: center

  .item
    margin: 0 20px 24px 0

    display: inline-block
    text-align: center

    color: inherit
    text-decoration: none

    cursor: pointer

    &:first-child
      margin-left: 24px

    &:hover
      color: currentcolor

    &.is-hidden
      display: none

  .icon
    display: inline-block
    width: 54px
    height: 54px

    border-radius: 50%

    background-size: cover, cover

  .label
    font-size: 0.875rem

.keywords
  margin: 0 16px 8px
  text-align: center

  .item
    margin: 0 3px
    display: inline-block

    color: #8E8E8E
    font-size: 0.75rem
    font-weight: bold

.desc
  margin: 0 16px 25px
  white-space: pre-wrap

  font-size: 0.875rem
  color: #8E8E8E

.meta
  .item
    display: flex
    padding: 8px 24px

    color: inherit

    &:hover
      color: currentcolor
      text-decoration: none

  .icon
    margin-right: 12px
    color: #BDBDBD

  .content
    flex-grow: 1

  .more
    margin-left: 12px
    color: #BBB

    transform-origin: center center

.branch-toggle
  cursor: pointer

  &.expended
    & > .more
      transform: rotateX(180deg)

.branches
  margin-left: 20px
  max-height: 0px
  overflow: hidden

  transition: max-height .25s cubic-bezier(0, 1, 0, 1)

  &.expended
    max-height: 10000px
    transition: max-height 1s ease-in-out

    & > .more
      transform: rotateX(180deg)

.store-toggle
  cursor: pointer

  &.expended
    & > .more
      transform: rotateX(180deg)

.store-meta
  margin-left: 20px
  max-height: 0px
  overflow: hidden

  transition: max-height .25s cubic-bezier(0, 1, 0, 1)

  &.expended
    max-height: 10000px
    transition: max-height 1s ease-in-out

.hours-toggle
  cursor: pointer

  &.expended
    & > .more
      transform: rotateX(180deg)

.hours
  margin-left: 36px
  max-height: 0px
  overflow: hidden

  transition: max-height .25s cubic-bezier(0, 1, 0, 1)

  &.expended
    max-height: 10000px
    transition: max-height 1s ease-in-out

.vips
  padding: 0 15px
  display: flex
  flex-direction: column

  .item
    height: 54px
    display: flex
    overflow: hidden
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, .15)
    margin-bottom: 16px

    border-radius: 10px

    background-color: white

    cursor: pointer

    &.current-vip
      order: -1

    .vip
      &-fig
        img
          width: 108px
          height: 54px

          border-radius: 10px

      &-info
        display: flex
        flex-direction: column
        justify-content: center

        padding-left: 12px

      &-name
        font-weight: bold
        font-size: 1rem

      &-meta
        font-size: 0.75rem
        color: #8E8E8E

.section
  &-download
    padding: 0 15px

    font-size: 0.75rem

    [lang=en] &
      font-size: 0.875rem

    .link
      color: #37474F

    .appicon
      width: 40px
      height: auto

    .cta
      display: inline-block
      margin-top: 4px
      padding: 2px 8px

      border-radius: 6px

      background-color: #f5f5f5

      color: #8E8E8E
      font-weight: bold

  &-tool
    padding: 24px 15px

    .profile
      padding: 8px

      font-size: 0.75rem
      color: #8E8E8E

    .btn-link
      margin: 4px
      padding: 8px 16px
      display: inline-block

      border-radius: 10px

      background: #f5f5f5

      text-align: center
      font-size: 0.75rem
      font-weight: bold
      color: #8E8E8E

      cursor: pointer

      &:hover,
      &:active
        color: #333

.section--history
  text-align: center

.history
  .item
    position: relative
    padding: 16px

    text-align: left

    &:not(:last-child)
      &::after
        position: absolute
        top: auto
        right: 24px
        bottom: 0
        left: 24px
        height: 1px

        background-color: #E6E6E6

        content: ''

  .more
    margin: 8px auto 0
    padding: 8px 16px
    display: inline-block

    border-radius: 10px

    background: #f5f5f5

    text-align: center
    font-size: 0.75rem
    font-weight: bold
    color: #8E8E8E

    cursor: pointer

  .content
    font-size: 0.875rem

  .time
    font-size: 0.75rem
    color: #8E8E8E

.gallery
  padding: 0 15px
  display: flex
  overflow-x: auto
  gap: 8px
  -webkit-overflow-scrolling: touch
  scrollbar-width: none
  -ms-overflow-style: none

  &::-webkit-scrollbar
    display: none

  .gallery-item
    flex: 0 0 auto
    width: 109px
    height: 109px
    position: relative
    overflow: hidden
    border-radius: 6px
    cursor: pointer
    background-color: #f5f5f5
    &::after
      content: ''
      position: absolute
      inset: 0
      border-radius: inherit
      box-shadow: inset 0 0 0 0.5px rgba(0, 0, 0, 0.1)
      pointer-events: none
      z-index: 1

    img
      width: 100%
      height: 100%
      object-fit: cover
      transition: transform 0.3s ease

    &:hover img
      transform: scale(1.05)

::v-deep
  .vue-lb-header
    height: auto
    margin-bottom: 16px
    padding-right: 24px
    justify-content: end

  .vue-lb-container
    padding: 0
    overflow-y: auto

  .vue-lb-content
    width: 100%
    margin: auto 0

  .vue-lb-modal-image
    width: 100%
    max-height: none

  @media (min-width: 768px)
    .vue-lb-content
      max-width: 50vw

  @media (min-width: 992px)
    .vue-lb-content
      max-width: 33.3333vw

  .vue-lb-arrow, .vue-lb-footer
    display: none

  .vue-lb-info
    left: 0
    bottom: 0
    height: auto
    padding: 10px
    background: none

    color: white
    font-weight: bold
    font-size: 12px
    text-align: start
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5)

  .vue-lb-button-close
    width: 40px
    height: 40px
    padding: 8px
    background-color: rgba(255, 255, 255, 0.1)

    border-radius: 50%

    color: white

    &:focus
      outline: none
</style>
